import { useRouterState } from '@tanstack/react-router';
import { shortId } from '@/lib/utils';
import { listHref, readListState, type ListStateDef, type UrlSafe } from './list-state';
import { HISTORY_LIST, REVIEW_LIST, WITHDRAWAL_LIST } from './ops-lists';

/**
 * Where a detail screen came from, and how it gets back.
 *
 * A transaction is reached from two different screens and a case from a third, so "back" is not a
 * property of the detail page — it is a property of the journey. The list screen therefore hands
 * its own view along as a `from` param, and the detail screen offers a link back to *that* view:
 * the same tab, the same filter, the same page the operator was reading.
 *
 * A return address is an instruction to navigate that arrives in a URL, so it is treated as
 * untrusted input twice over:
 *
 * - **The path must be one of the origins below**, matched exactly — or a deposit detail path,
 *   matched against the id grammar. Nothing else is a return address — not another route, not a
 *   path that merely starts with one of these, and not an absolute or protocol-relative URL, so
 *   `from` cannot be used to bounce an operator off-site.
 * - **The query is rebuilt, never echoed.** The origin's own readers parse it and its own writer
 *   renders it again, so whatever survives is a view that screen actually offers. Anything else
 *   is dropped on the way through.
 *
 * A detail screen opened without a usable `from` — pasted link, refreshed tab, hand-typed URL —
 * falls back to the origin it belongs to, which is what the back link used to do unconditionally.
 *
 * One detail screen is also a return address: a deposit's own page. The split-fill lineage on it
 * lists the *other* deposits that filled the same withdrawal, and following one of those is a
 * sideways hop between siblings, not a descent from a list — an operator comparing two slices has
 * to be able to get back to the one they were reading. Its query is dropped rather than rebuilt,
 * so `from` chains cannot nest: the hop remembers one step, which is the step that was taken.
 */

/** How long a `from` param may be. Past any canonical list href, short of a payload. */
const FROM_MAX_LENGTH = 512;

/** A list screen a detail screen may return to. */
export interface ReturnOrigin {
  /** The screen's path — the only value of `from` this origin will match. */
  readonly path: string;
  /** i18n key for "Back to …". */
  readonly labelKey: string;
  /** Rebuild this screen's canonical href from an untrusted query string. */
  readonly canonicalHref: (searchStr: string) => string;
}

/** Where a back link points and what it says. */
export interface ReturnTarget {
  /** A canonical in-app path. */
  readonly href: string;
  /** i18n key for the link's text. */
  readonly labelKey: string;
  /** Interpolation for {@link labelKey}, when the label names the thing it returns to. */
  readonly labelParams?: Readonly<Record<string, string>>;
}

/** The `from` param, once validated. Absent when the URL carried nothing usable. */
export interface ReturnSearch {
  /** A canonical origin href. */
  readonly from?: string;
}

/** Turn a list screen's URL contract into an origin a detail screen may return to. */
function origin<TState extends UrlSafe<TState>>(
  def: ListStateDef<TState>,
  labelKey: string,
): ReturnOrigin {
  return {
    path: def.path,
    labelKey,
    canonicalHref: (searchStr) => listHref(def, readListState(def, searchStr)),
  };
}

/** The withdrawal queue as a return origin. */
export const WITHDRAWALS_ORIGIN = origin(WITHDRAWAL_LIST, 'nav.backTo.withdrawals');

/** History as a return origin. */
export const HISTORY_ORIGIN = origin(HISTORY_LIST, 'nav.backTo.history');

/** The review queue as a return origin. */
export const REVIEWS_ORIGIN = origin(REVIEW_LIST, 'nav.backTo.reviews');

/** Every list screen a `from` param is allowed to name. */
const ORIGINS: readonly ReturnOrigin[] = [WITHDRAWALS_ORIGIN, HISTORY_ORIGIN, REVIEWS_ORIGIN];

/**
 * A deposit detail screen, as a `from` may spell it.
 *
 * Anchored, and the id is matched against the contracts' own `txn_<ulid>` shape rather than
 * "whatever follows the slash": the href is handed straight back as the destination, so the
 * charset that decides it is the one the id grammar allows and nothing else.
 */
const TRANSACTION_PATH = /^\/ops\/history\/(txn_[0-9A-HJKMNP-TV-Z]{26})$/;

/** Build the `from` a deposit detail screen hands to the next one — its own canonical path. */
export function transactionReturnHref(transactionId: string): string {
  return `/ops/history/${transactionId}`;
}

/** Read a `from` param into a back-link target, or `null` if it names nowhere we return to. */
export function decodeReturnTo(value: unknown): ReturnTarget | null {
  if (typeof value !== 'string' || value === '' || value.length > FROM_MAX_LENGTH) return null;
  const queryAt = value.indexOf('?');
  const path = queryAt === -1 ? value : value.slice(0, queryAt);
  const searchStr = queryAt === -1 ? '' : value.slice(queryAt + 1);
  const match = ORIGINS.find((candidate) => candidate.path === path);
  if (match !== undefined) {
    return { href: match.canonicalHref(searchStr), labelKey: match.labelKey };
  }
  const transaction = TRANSACTION_PATH.exec(path);
  if (transaction !== null) {
    // The path alone, never the query it arrived with: a sibling hop remembers one step, and
    // echoing a `from` of its own through here is how a return address grows without bound.
    return {
      href: path,
      labelKey: 'nav.backTo.transaction',
      labelParams: { id: shortId(transaction[1] ?? '') },
    };
  }
  return null;
}

/**
 * `validateSearch` for a detail route: keep `from` only if it is a real return address, and keep
 * it canonicalised, so the value the router carries is one this app produced.
 */
export function validateReturnSearch(search: Record<string, unknown>): ReturnSearch {
  const target = decodeReturnTo(search['from']);
  return target === null ? {} : { from: target.href };
}

/** Where this detail screen's back link goes: the screen it was opened from, or `fallback`. */
export function useReturnTarget(fallback: ReturnOrigin): ReturnTarget {
  const searchStr = useRouterState({ select: (state) => state.location.searchStr });
  return (
    decodeReturnTo(new URLSearchParams(searchStr).get('from')) ?? {
      href: fallback.path,
      labelKey: fallback.labelKey,
    }
  );
}
